import { useState, useMemo } from "react";
import { format, isSameDay, parseISO } from "date-fns";
import { Download, FileText, FileJson, User, Bot, Filter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn, initials, avatarColor } from "@/lib/utils";
import { LargeReviewModal } from "@/components/ui/large-review-modal";
import { toast } from "sonner";
import { useJobMessagesAll } from "@/hooks/useJobMessagesAll";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { sanitizeMessageHtml } from "@/lib/sanitizeMessageHtml";
import type { JobMessage, MessageCategory, SenderRole } from "@/data/jobMessageTypes";

type RoleFilter = "all" | "user" | "assistant";

interface ConversationHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobNumber?: string;
}

const CATEGORY_LABELS: Record<MessageCategory, string> = {
  orders: "Orders",
  concerns: "Concerns",
  general: "General",
};

const CATEGORY_COLORS: Record<MessageCategory, string> = {
  orders: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  concerns: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  general: "bg-slate-500/10 text-slate-600 border-slate-500/20",
};

const SYSTEM_ROLES: ReadonlySet<SenderRole> = new Set(["system"]);

function isAssistantMessage(msg: JobMessage): boolean {
  return SYSTEM_ROLES.has(msg.senderRole);
}

function getSenderCategory(msg: JobMessage): "user" | "assistant" {
  return isAssistantMessage(msg) ? "assistant" : "user";
}

export function ConversationHistoryModal({
  open,
  onOpenChange,
  jobId,
  jobNumber,
}: ConversationHistoryModalProps) {
  const { profile } = useCurrentUser();
  const currentUserId = profile?.id ?? "";
  const { messages, isLoading, error } = useJobMessagesAll(jobId);
  const [categoryFilter, setCategoryFilter] = useState<MessageCategory | "all">("all");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");

  const filtered = useMemo(() => {
    return messages
      .slice()
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .filter((msg) => {
        if (categoryFilter !== "all" && msg.category !== categoryFilter) return false;
        if (roleFilter === "user" && isAssistantMessage(msg)) return false;
        if (roleFilter === "assistant" && !isAssistantMessage(msg)) return false;
        return true;
      });
  }, [messages, categoryFilter, roleFilter]);

  const groupedByDate = useMemo(() => {
    const groups: Array<{ date: string; messages: JobMessage[] }> = [];
    let currentGroup: (typeof groups)[0] | null = null;

    for (const msg of filtered) {
      const ts = new Date(msg.timestamp);
      const dateStr = format(ts, "yyyy-MM-dd");
      if (!currentGroup || currentGroup.date !== dateStr) {
        currentGroup = { date: dateStr, messages: [] };
        groups.push(currentGroup);
      }
      currentGroup.messages.push(msg);
    }
    return groups;
  }, [filtered]);

  const handleExportText = () => {
    if (filtered.length === 0) {
      toast.warning("No messages to export");
      return;
    }
    const lines: string[] = [];
    lines.push(`Conversation History — ${jobNumber ?? jobId}`);
    lines.push(`Exported: ${format(new Date(), "PPP p")}`);
    lines.push("");

    for (const group of groupedByDate) {
      lines.push(
        format(parseISO(group.date), "EEEE, MMMM d, yyyy"),
      );
      lines.push("-".repeat(60));

      for (const msg of group.messages) {
        const cat = CATEGORY_LABELS[msg.category] ?? msg.category;
        const role = getSenderCategory(msg);
        const when = format(new Date(msg.timestamp), "h:mm a");
        const senderLabel = `${msg.senderName}${msg.senderRole ? ` (${msg.senderRole})` : ""}`;
        lines.push(`[${when}] [${cat}] [${role}] ${senderLabel}:`);
        const plain = msg.body.replace(/<[^>]*>/g, "").trim();
        lines.push(plain || "(no text)");
        lines.push("");
      }
      lines.push("");
    }

    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversation_${jobNumber ?? jobId}_${format(new Date(), "yyyyMMdd_HHmmss")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported conversation as .txt");
  };

  const handleExportJson = () => {
    if (filtered.length === 0) {
      toast.warning("No messages to export");
      return;
    }
    const payload = {
      jobId,
      jobNumber,
      exportedAt: new Date().toISOString(),
      count: filtered.length,
      messages: filtered.map((msg) => ({
        id: msg.id,
        category: msg.category,
        senderCategory: getSenderCategory(msg),
        senderId: msg.senderId,
        senderName: msg.senderName,
        senderRole: msg.senderRole,
        timestamp: msg.timestamp,
        priority: msg.priority,
        body: msg.body,
        bodyText: msg.body.replace(/<[^>]*>/g, "").trim(),
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversation_${jobNumber ?? jobId}_${format(new Date(), "yyyyMMdd_HHmmss")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported conversation as .json");
  };

  return (
    <LargeReviewModal
      open={open}
      onOpenChange={onOpenChange}
      title="Conversation History"
      description={
        jobNumber ? `Job ${jobNumber}` : `Job ${jobId}`
      }
      size="xl"
      stickyFooter
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleExportText}>
            <FileText className="h-4 w-4 mr-1.5" />
            Export .txt
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportJson}>
            <FileJson className="h-4 w-4 mr-1.5" />
            Export .json
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-muted-foreground">Category:</span>
            {(["all", "orders", "concerns", "general"] as const).map((cat) => (
              <Badge
                key={cat}
                variant={categoryFilter === cat ? "default" : "outline"}
                className={cn(
                  "cursor-pointer text-xs",
                  categoryFilter === cat && cat !== "all" && CATEGORY_COLORS[cat as MessageCategory],
                )}
                onClick={() => setCategoryFilter(cat)}
              >
                {cat === "all" ? "All" : CATEGORY_LABELS[cat as MessageCategory]}
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-muted-foreground">Role:</span>
            {(["all", "user", "assistant"] as const).map((r) => (
              <Badge
                key={r}
                variant={roleFilter === r ? "default" : "outline"}
                className="cursor-pointer text-xs capitalize"
                onClick={() => setRoleFilter(r)}
              >
                {r === "all" ? "All" : r === "user" ? "User" : "Assistant"}
              </Badge>
            ))}
          </div>
        </div>

        {error ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Failed to load conversation history.
          </div>
        ) : isLoading ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Loading conversation history...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No messages match the selected filters.
          </div>
        ) : (
          <div className="space-y-6">
            {groupedByDate.map((group) => (
              <div key={group.date}>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  {format(parseISO(group.date), "EEEE, MMMM d, yyyy")}
                </div>
                <div className="space-y-3">
                  {group.messages.map((msg) => {
                    const role = getSenderCategory(msg);
                    const isOwn = msg.senderId === currentUserId;
                    const catColor = CATEGORY_COLORS[msg.category] ?? CATEGORY_COLORS.general;
                    const bgClass = isAssistantMessage(msg)
                      ? "bg-muted/40"
                      : "bg-background border border-border";

                    return (
                      <div
                        key={msg.id}
                        className={cn(
                          "relative rounded-lg px-3 py-2.5",
                          role === "user" && isOwn
                            ? "ml-auto bg-primary text-primary-foreground"
                            : role === "user"
                            ? "mr-auto"
                            : "mr-auto",
                          bgClass,
                          role === "assistant" && "border-l-2 border-primary/30",
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <div
                            className={cn(
                              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                              role === "assistant"
                                ? "bg-primary/10 text-primary"
                                : "bg-secondary text-secondary-foreground",
                            )}
                            style={
                              role === "user" && isOwn
                                ? {}
                                : { backgroundColor: avatarColor(msg.senderId) }
                            }
                          >
                            {role === "assistant" ? (
                              <Bot className="h-3.5 w-3.5" />
                            ) : (
                              <span className={role === "user" && isOwn ? "" : "text-white"}>
                                {initials(msg.senderName || "U")}
                              </span>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className={cn(
                                    "text-xs font-medium",
                                    role === "user" && isOwn
                                      ? "text-primary-foreground/90"
                                      : "text-foreground",
                                  )}
                                >
                                  {msg.senderName}
                                </span>
                                {msg.senderRole && (
                                  <span
                                    className={cn(
                                      "text-[10px]",
                                      role === "user" && isOwn
                                        ? "text-primary-foreground/70"
                                        : "text-muted-foreground",
                                    )}
                                  >
                                    · {msg.senderRole}
                                  </span>
                                )}
                                <Badge
                                  variant="outline"
                                  className={cn("text-[9px] h-4 px-1", catColor)}
                                >
                                  {CATEGORY_LABELS[msg.category] ?? msg.category}
                                </Badge>
                                <span
                                  className={cn(
                                    "text-[10px]",
                                    role === "user" && isOwn
                                      ? "text-primary-foreground/70"
                                      : "text-muted-foreground",
                                  )}
                                >
                                  {role === "user" ? "· User" : "· Assistant"}
                                </span>
                              </div>
                              <span
                                className={cn(
                                  "text-[10px]",
                                  role === "user" && isOwn
                                    ? "text-primary-foreground/70"
                                    : "text-muted-foreground",
                                )}
                              >
                                {format(new Date(msg.timestamp), "MMM d, h:mm a")}
                              </span>
                            </div>

                            <div
                              className="mt-1 text-sm break-words"
                              dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(msg.body) }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </LargeReviewModal>
  );
}
